import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Markdown, type TextFormat } from './markdown';
import { useMeta } from './meta';
import type { CustomField, IssueHeader } from './types';
import { t, tError, tp } from './i18n';

// ---------------------------------------------------------------------------
// formatting helpers
export function fmtDate(v: string | null | undefined, withTime = true): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return withTime ? `${date} ${pad(d.getHours())}:${pad(d.getMinutes())}` : date;
}

export function relTime(v: string | null | undefined): string {
  if (!v) return '';
  const diff = (Date.now() - new Date(v).getTime()) / 1000;
  if (diff < 60) return t('just now');
  if (diff < 3600) return tp(Math.floor(diff / 60), '{n} minute ago', '{n} minutes ago');
  if (diff < 86400) return tp(Math.floor(diff / 3600), '{n} hour ago', '{n} hours ago');
  if (diff < 86400 * 2) return t('yesterday');
  if (diff < 86400 * 30) return tp(Math.floor(diff / 86400), '{n} day ago', '{n} days ago');
  return fmtDate(v, false);
}

const oneDecimal = (v: number) => v.toFixed(1).replace(/\.0$/, '');
export const fmtBytes = (n: number) =>
  n < 1024
    ? `${n} B`
    : n < 1024 * 1024
      ? `${oneDecimal(n / 1024)} KB`
      : n < 1024 ** 3
        ? `${oneDecimal(n / 1024 / 1024)} MB`
        : `${oneDecimal(n / 1024 ** 3)} GB`;
/** Byte sizes given in MB: "100 MB", "10 GB". */
export const fmtMb = (mb: number) => (mb >= 1024 && mb % 1024 === 0 ? `${mb / 1024} GB` : `${mb} MB`);
export const fmtMinutes = (m: number) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
/** Display key of an issue: the Mantis-style 7-digit number people know from Mantis. */
export const issueNo = (id: number) => String(id).padStart(7, '0');

// ---------------------------------------------------------------------------
// avatars
const AVATAR_COLORS = ['#0C66E4', '#1F845A', '#C9372C', '#8F7EE7', '#E56910', '#1D7AFC', '#5E4DB2', '#22A06B', '#CA3521', '#0055CC', '#943D73', '#206A83'];
function hash(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
export function initials(name: string) {
  const parts = name.replace(/[._-]+/g, ' ').trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? parts[parts.length - 1][0] : (parts[0]?.[1] ?? ''))).toUpperCase();
}

export function Avatar({ name, size = 24, title }: { name?: string | null; size?: number; title?: string }) {
  if (!name) {
    return (
      <span className="avatar empty" style={{ width: size, height: size }} title={title ?? t('Unassigned')}>
        <svg viewBox="0 0 24 24" width={size * 0.62} height={size * 0.62} aria-hidden>
          <circle cx="12" cy="8" r="4" fill="currentColor" />
          <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" fill="currentColor" />
        </svg>
      </span>
    );
  }
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, background: AVATAR_COLORS[hash(name) % AVATAR_COLORS.length], fontSize: Math.max(9, size * 0.4) }}
      title={title ?? name}
    >
      {initials(name)}
    </span>
  );
}

export function UserChip({ name, sub, size = 24 }: { name?: string | null; sub?: string; size?: number }) {
  // nobody (e.g. not assigned): a plain dash, so the real names stand out
  if (!name) return <span className="subtle user-none" title={t('Unassigned')} aria-label={t('Unassigned')}>–</span>;
  return (
    <span className="user-chip">
      <Avatar name={name} size={size} />
      <span>
        {name}
        {sub && <span className="subtle small"> {sub}</span>}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// status lozenge
const LOZENGE: Record<number, string> = { 10: 'new', 20: 'feedback', 30: 'ack', 40: 'confirmed', 50: 'assigned', 80: 'resolved', 90: 'closed' };
export const statusClass = (s: number) => `lz-${LOZENGE[s] ?? 'new'}`;

export function StatusBadge({ status, resolution }: { status: number; resolution?: number }) {
  const { label } = useMeta();
  const res = resolution !== undefined && status >= 80 && resolution !== 20 ? label('resolution', resolution) : '';
  return (
    <span className={`lozenge ${statusClass(status)}`} title={res ? `${label('status', status)} (${res})` : undefined}>
      {label('status', status)}
      {res && <span className="lz-res"> · {res}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// issue type (from Mantis severity) and priority icons
export function TypeIcon({ severity, size = 16 }: { severity: number; size?: number }) {
  const { label } = useMeta();
  const title = label('severity', severity);
  if (severity === 10) {
    return (
      <svg className="type-icon" width={size} height={size} viewBox="0 0 16 16" aria-label={title}>
        <title>{`${t('Feature')} (${title})`}</title>
        <rect width="16" height="16" rx="3" fill="#63BA3C" />
        <path d="M5 4h6v8.5L8 10.4 5 12.5z" fill="#fff" />
      </svg>
    );
  }
  if (severity <= 40) {
    return (
      <svg className="type-icon" width={size} height={size} viewBox="0 0 16 16" aria-label={title}>
        <title>{`${t('Task')} (${title})`}</title>
        <rect width="16" height="16" rx="3" fill="#4BADE8" />
        <path d="M4.5 8.2l2.3 2.3 4.7-5" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg className="type-icon" width={size} height={size} viewBox="0 0 16 16" aria-label={title}>
      <title>{`${t('Bug')} (${title})`}</title>
      <rect width="16" height="16" rx="3" fill={severity >= 70 ? '#C9372C' : '#E5493A'} />
      <circle cx="8" cy="8" r="3.4" fill="#fff" />
    </svg>
  );
}

export function PriorityIcon({ priority, size = 16 }: { priority: number; size?: number }) {
  const { label } = useMeta();
  const title = `${t('Priority')}: ${label('priority', priority)}`;
  const up = (color: string, double = false) => (
    <svg className="prio-icon" width={size} height={size} viewBox="0 0 16 16" aria-label={title}>
      <title>{title}</title>
      <path d={double ? 'M3.5 9.5L8 5l4.5 4.5M3.5 13L8 8.5l4.5 4.5' : 'M3.5 10.5L8 6l4.5 4.5'} stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const down = (color: string, double = false) => (
    <svg className="prio-icon" width={size} height={size} viewBox="0 0 16 16" aria-label={title}>
      <title>{title}</title>
      <path d={double ? 'M3.5 3L8 7.5 12.5 3M3.5 6.5L8 11l4.5-4.5' : 'M3.5 5.5L8 10l4.5-4.5'} stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  switch (priority) {
    case 60:
      return up('#C9372C', true);
    case 50:
      return up('#C9372C');
    case 40:
      return up('#E56910');
    case 20:
      return down('#1D7AFC');
    case 10:
      return down('#1D7AFC', true);
    default:
      return (
        <svg className="prio-icon" width={size} height={size} viewBox="0 0 16 16" aria-label={title}>
          <title>{title}</title>
          <path d="M3.5 6h9M3.5 10h9" stroke="#E2B203" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
  }
}

// ---------------------------------------------------------------------------
export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return <div className="banner error">{tError((error as Error).message ?? String(error))}</div>;
}

/**
 * A dangerous action (deleting a project, team or account): the button opens a form where the user types
 * `confirmText` (e.g. the team name) and, with `needPassword`, their password.
 */
export function DangerZone({
  title,
  children,
  button,
  confirmText,
  needPassword,
  disabled,
  onConfirm,
}: {
  title: ReactNode;
  children: ReactNode;
  button: string;
  confirmText: string;
  needPassword?: boolean;
  disabled?: boolean;
  onConfirm: (password: string) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const matches = typed.trim().toLowerCase() === confirmText.trim().toLowerCase();
  const reset = () => {
    setOpen(false);
    setTyped('');
    setPassword('');
    setError(null);
  };
  return (
    <Panel title={title} className="danger-zone">
      {children}
      {!open ? (
        <button type="button" className="danger" disabled={disabled} onClick={() => setOpen(true)}>
          {button}
        </button>
      ) : (
        <form
          className="danger-confirm"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!matches || (needPassword && !password)) return;
            setPending(true);
            setError(null);
            try {
              await onConfirm(password);
            } catch (err) {
              setError(err);
            } finally {
              setPending(false);
            }
          }}
        >
          <label className="block">
            {t('Type {name} to confirm', { name: confirmText })}
            <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </label>
          {needPassword && (
            <label className="block">
              {t('Your password')}
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </label>
          )}
          <ErrorBox error={error} />
          <div className="inline-form">
            <button className="danger-solid" disabled={!matches || (needPassword && !password) || pending}>
              {button}
            </button>
            <button type="button" onClick={reset}>
              {t('Cancel')}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

export function Panel({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className ?? ''}`}>
      {(title || actions) && (
        <div className="card-head">
          <h2>{title}</h2>
          <div className="card-actions">{actions}</div>
        </div>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

export function useClickOutside(ref: React.RefObject<HTMLElement | null>, onOutside: () => void, active = true) {
  useEffect(() => {
    if (!active) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside();
    };
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onOutside();
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', k);
    };
  }, [ref, onOutside, active]);
}

/** Button that opens a popover menu. */
export function Dropdown({
  trigger,
  children,
  align = 'left',
  className,
}: {
  trigger: (open: boolean) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);
  return (
    <div className={`dropdown ${className ?? ''}`} ref={ref}>
      <span onClick={() => setOpen(!open)}>{trigger(open)}</span>
      {open && <div className={`menu ${align}`}>{children(() => setOpen(false))}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, width = 640 }: { title: ReactNode; onClose: () => void; children: ReactNode; width?: number }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', k);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', k);
      document.body.classList.remove('modal-open');
    };
  }, [onClose]);
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: width }} role="dialog" aria-modal="true">
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t('Close')}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Mantis-style text: keeps line breaks, links URLs, #123 (issue) and ~123 (note).
/** Description, note or version text, formatted (see markdown.tsx). */
export function FormattedText({ text, className, format }: { text: string; className?: string; format?: TextFormat }) {
  if (!text) return null;
  return (
    <div className={`formatted ${className ?? ''}`}>
      <Markdown text={text} format={format} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// issue table
export interface Column {
  key: string;
  label: string;
  sort?: string;
  render: (i: IssueHeader) => ReactNode;
  className?: string;
}

export function useIssueColumns(opts: { showProject: boolean }): Column[] {
  return [
    { key: 'type', label: t('Type'), sort: 'severity', render: (i) => <TypeIcon severity={i.severity} />, className: 'narrow center' },
    {
      key: 'id',
      label: t('Key'),
      sort: 'id',
      render: (i) => (
        <Link to={`/issues/${i.id}`} className="key">
          {issueNo(i.id)}
        </Link>
      ),
      className: 'nowrap',
    },
    {
      key: 'summary',
      label: t('Summary'),
      sort: 'summary',
      render: (i) => (
        <span className="summary-cell">
          <Link to={`/issues/${i.id}`} className="summary-link">
            {i.summary}
          </Link>
          {i.view_state === 50 && <span className="tag-mini private">{t('private')}</span>}
          {i.sticky && <span className="tag-mini">{t('pinned')}</span>}
          {opts.showProject && <span className="subtle small"> · {i.project_name}</span>}
          {i.category_name && <span className="tag-mini">{i.category_name}</span>}
        </span>
      ),
      className: 'wide',
    },
    { key: 'handler', label: t('Assignee'), sort: 'handler', render: (i) => <UserChip name={i.handler_name} />, className: 'nowrap' },
    { key: 'reporter', label: t('Reporter'), sort: 'reporter', render: (i) => <UserChip name={i.reporter_name} />, className: 'nowrap hide-sm' },
    { key: 'prio', label: t('P'), sort: 'priority', render: (i) => <PriorityIcon priority={i.priority} />, className: 'narrow center' },
    { key: 'status', label: t('Status'), sort: 'status', render: (i) => <StatusBadge status={i.status} resolution={i.resolution} />, className: 'nowrap' },
    {
      key: 'activity',
      label: '',
      render: (i) => (
        <span className="subtle small nowrap">
          {i.note_count > 0 && <span title={t('Comments')}>💬 {i.note_count}</span>} {i.attachment_count > 0 && <span title={t('Attachments')}>📎 {i.attachment_count}</span>}
        </span>
      ),
      className: 'nowrap hide-sm',
    },
    { key: 'updated', label: t('Updated'), sort: 'updated', render: (i) => <span title={fmtDate(i.updated_at)}>{relTime(i.updated_at)}</span>, className: 'nowrap subtle' },
  ];
}

/** Row class/style that tints an issue row by its status, honouring the "color issue rows" preference. */
export function useIssueRowColor() {
  const { meta, statusColor } = useMeta();
  const colorRows = meta.prefs?.color_issue_rows !== false;
  return (status: number) =>
    colorRows ? { className: 'issue-status-row', style: { '--issue-status-color': statusColor(status) } as CSSProperties } : { className: '', style: undefined };
}

export function IssueTable({
  issues,
  columns,
  sort,
  dir,
  onSort,
  selected,
  onSelect,
  stickyFirst = false,
}: {
  issues: IssueHeader[];
  columns: Column[];
  sort?: string;
  dir?: string;
  onSort?: (key: string) => void;
  selected?: Set<number>;
  onSelect?: (ids: Set<number>) => void;
  stickyFirst?: boolean;
  compact?: boolean;
}) {
  const { meta, statusColor } = useMeta();
  const colorRows = meta.prefs?.color_issue_rows !== false;
  const allSelected = !!selected && issues.length > 0 && issues.every((i) => selected.has(i.id));
  const pinnedDividerIndex = stickyFirst ? issues.findIndex((i) => !i.sticky) : -1;
  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            {onSelect && (
              <th className="narrow">
                <input type="checkbox" checked={allSelected} aria-label={t('Select all')} onChange={(e) => onSelect(new Set(e.target.checked ? issues.map((i) => i.id) : []))} />
              </th>
            )}
            {columns.map((c) => (
              <th key={c.key} className={c.className}>
                {c.sort && onSort ? (
                  <button className="th-sort" onClick={() => onSort(c.sort!)}>
                    {c.label}
                    <span className="sort-arrow">{sort === c.sort ? (dir === 'asc' ? '↑' : '↓') : ''}</span>
                  </button>
                ) : (
                  c.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {issues.map((i, index) => (
            <tr
              key={i.id}
              className={`${colorRows ? 'issue-status-row' : ''} ${selected?.has(i.id) ? 'selected' : ''} ${pinnedDividerIndex > 0 && index === pinnedDividerIndex ? 'pinned-divider' : ''}`.trim()}
              style={colorRows ? { '--issue-status-color': statusColor(i.status) } as CSSProperties : undefined}
            >
              {onSelect && (
                <td className="narrow">
                  <input
                    type="checkbox"
                    checked={selected!.has(i.id)}
                    aria-label={t('Select {id}', { id: i.id })}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(i.id);
                      else next.delete(i.id);
                      onSelect(next);
                    }}
                  />
                </td>
              )}
              {columns.map((c) => (
                <td key={c.key} className={c.className}>
                  {c.render(i)}
                </td>
              ))}
            </tr>
          ))}
          {issues.length === 0 && (
            <tr>
              <td colSpan={columns.length + (onSelect ? 1 : 0)} className="empty-row">
                {t('No issues match your filters.')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Pager({ page, perPage, total, onPage, compact }: { page: number; perPage: number; total: number; onPage: (p: number) => void; compact?: boolean }) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages <= 1) return null;
  const nums: number[] = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(pages, page + 2); p++) nums.push(p);
  return (
    <div className={`pager ${compact ? 'compact' : ''}`}>
      {nums[0] > 1 && (
        <>
          <button className="btn subtle" onClick={() => onPage(1)}>
            1
          </button>
          {nums[0] > 2 && <span className="subtle">…</span>}
        </>
      )}
      {nums.map((p) => (
        <button key={p} className={`btn subtle ${p === page ? 'current' : ''}`} onClick={() => onPage(p)}>
          {p}
        </button>
      ))}
      {nums[nums.length - 1] < pages && (
        <>
          {nums[nums.length - 1] < pages - 1 && <span className="subtle">…</span>}
          <button className="btn subtle" onClick={() => onPage(pages)}>
            {pages}
          </button>
        </>
      )}
      {/* previous and next side by side at the end: they stay in place whatever the number of page buttons */}
      <span className="pager-steps">
        <button className="btn subtle" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label={t('Previous page')} title={t('Previous page')}>
          ‹
        </button>
        <button className="btn subtle" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label={t('Next page')} title={t('Next page')}>
          ›
        </button>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom field editor. Multi-value fields are stored Mantis-style as "|a|b|".
export const splitMulti = (v: string) => v.split('|').filter(Boolean);
export const joinMulti = (vals: string[]) => (vals.length ? `|${vals.join('|')}|` : '');

export function CustomFieldInput({ field, value, onChange, disabled }: { field: CustomField; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const options = field.possible_values ? field.possible_values.split('|') : [];
  switch (field.type) {
    case 3:
    case 6:
      return (
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
          <option value="">{t('None')}</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case 9:
      return (
        <span className="radios">
          {options.map((o) => (
            <label key={o}>
              <input type="radio" checked={value === o} onChange={() => onChange(o)} disabled={disabled} /> {o}
            </label>
          ))}
        </span>
      );
    case 5:
    case 7: {
      const cur = new Set(splitMulti(value));
      return (
        <span className="radios">
          {options.map((o) => (
            <label key={o}>
              <input
                type="checkbox"
                checked={cur.has(o)}
                disabled={disabled}
                onChange={(e) => onChange(joinMulti(options.filter((x) => (x === o ? e.target.checked : cur.has(x)))))}
              />{' '}
              {o}
            </label>
          ))}
        </span>
      );
    }
    case 8: {
      const d = value && /^\d+$/.test(value) ? new Date(Number(value) * 1000).toISOString().slice(0, 10) : '';
      return (
        <input
          type="date"
          value={d}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value ? String(Math.floor(new Date(e.target.value + 'T00:00:00').getTime() / 1000)) : '')}
        />
      );
    }
    case 10:
      return <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
    case 1:
    case 2:
      return <input type="number" step={field.type === 2 ? 'any' : 1} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
    case 4:
      return <input type="email" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
    default:
      return <input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} maxLength={field.length_max || undefined} />;
  }
}

export function customFieldDisplay(field: CustomField, value: string): string {
  if (!value) return '';
  if (field.type === 5 || field.type === 7) return splitMulti(value).join(', ');
  if (field.type === 8 && /^\d+$/.test(value)) return fmtDate(new Date(Number(value) * 1000).toISOString(), false);
  return value;
}
